/**
 * WeatherBackdrop.jsx
 *
 * Apple-Weather-style animated backdrop for the Dashboard page.
 *
 * ── Design ──
 * A layered scene painted entirely with CSS + SVG + Canvas (no video, no
 * copyrighted assets) that mimics Apple Weather's atmosphere:
 *
 *   1. Sky gradient   → interpolates through five key colour stops
 *      (deep-night → dawn → midday → dusk → night) driven by the real
 *      sun position for Plettenberg Bay, so the backdrop's ambient
 *      light matches the light outside the window.
 *   2. Sun / moon     → positioned along an arc based on how far we
 *      are through the day (or night).
 *   3. Star field     → SVG dots that fade in below the horizon after
 *      civil twilight ends.
 *   4. Cloud layer    → SVG cloud shapes with opacity + density scaled
 *      by `cloud_cover_pct`. They drift horizontally at a wind-derived
 *      speed.
 *   5. Precip layer   → Canvas rain streaks or snowflakes activated by
 *      the WMO weather code (drizzle / rain / snow / thunder).
 *   6. Fog / mist     → soft radial gradient overlay for WMO 45/48.
 *
 * All layers respect:
 *   • `prefers-reduced-motion`  → animations pause.
 *   • Dark mode                  → sky gradient shifts to cooler tones.
 *
 * Mounted ONLY on the Dashboard page (parent controls this). The
 * backdrop is `position:fixed` behind the app shell content with
 * z-index: -1 so all existing UI stays clickable.
 *
 * Data contract with `/api/weather`:
 *   { weather_code, is_day, cloud_cover_pct, sunrise, sunset,
 *     wind_kmh, temperature_c, ... }
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";

// ── Sky palette ─────────────────────────────────────────────────────────
// Palette is keyed by *offset in minutes* from the closest civil event
// (sunrise or sunset), NOT by fraction-of-day. This means dawn / dusk
// glow lasts about 30 minutes each in real wall-clock time, matching
// what you'd actually see in the sky over Plett rather than being
// stretched or compressed by the length of the night.
//
// The `phase` axis is normalized 0..1 where:
//    0.00 = deep night (>= 90 min after sunset AND >= 90 min before sunrise)
//    0.22 = 60 min before sunrise (astronomical→nautical twilight)
//    0.27 = 30 min before sunrise (nautical→civil dawn glow)
//    0.35 = 30 min after sunrise (golden-hour morning)
//    0.50 = solar noon
//    0.68 = 30 min before sunset (golden-hour afternoon)
//    0.75 = 15 min after sunset (sunset glow lingering)
//    0.82 = 60 min after sunset (nautical twilight, deep violet)
//    1.00 = deep night
const SKY_PHASES = [
  { t: 0.00, top: "hsl(230, 55%, 5%)",  mid: "hsl(232, 45%, 9%)",  bot: "hsl(238, 40%, 14%)" }, // deep night
  { t: 0.22, top: "hsl(232, 45%, 12%)", mid: "hsl(258, 40%, 20%)", bot: "hsl(20, 55%, 35%)" },  // pre-dawn
  { t: 0.27, top: "hsl(210, 65%, 45%)", mid: "hsl(24, 75%, 55%)",  bot: "hsl(38, 90%, 68%)" },  // sunrise glow
  { t: 0.35, top: "hsl(205, 82%, 55%)", mid: "hsl(200, 72%, 70%)", bot: "hsl(195, 62%, 82%)" }, // morning
  { t: 0.50, top: "hsl(210, 90%, 52%)", mid: "hsl(205, 80%, 65%)", bot: "hsl(200, 65%, 82%)" }, // midday
  { t: 0.68, top: "hsl(215, 60%, 55%)", mid: "hsl(15, 75%, 62%)",  bot: "hsl(35, 85%, 70%)" },  // late afternoon
  { t: 0.75, top: "hsl(232, 60%, 24%)", mid: "hsl(6, 65%, 40%)",   bot: "hsl(28, 80%, 55%)" },  // sunset glow (fades quicker)
  { t: 0.82, top: "hsl(240, 50%, 12%)", mid: "hsl(258, 40%, 20%)", bot: "hsl(288, 35%, 22%)" }, // nautical twilight
  { t: 1.00, top: "hsl(230, 55%, 5%)",  mid: "hsl(232, 45%, 9%)",  bot: "hsl(238, 40%, 14%)" }, // deep night (loop)
];

function parseHsl(str) {
  const m = str.match(/hsl\((-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)%,\s*(-?\d+(?:\.\d+)?)%\)/);
  if (!m) return [0, 0, 0];
  return [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3])];
}

function lerp(a, b, t) { return a + (b - a) * t; }

/** Interpolate two HSL colors along the SHORTER arc of the hue wheel.
 *  Fixes the "green sky at dusk" bug: linear lerp of hue 32 → 288
 *  crosses through 100/150/200 (green/cyan) even though the visual
 *  path we want is the short way round (32 → 0 → 340 → 288 = -104°).
 */
function lerpHsl(a, b, t) {
  const [h1, s1, l1] = parseHsl(a);
  const [h2, s2, l2] = parseHsl(b);
  // Shortest hue arc: if the delta is > 180° we go the other way.
  let delta = h2 - h1;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  const h = ((h1 + delta * t) % 360 + 360) % 360;
  return `hsl(${h.toFixed(1)}, ${lerp(s1, s2, t).toFixed(1)}%, ${lerp(l1, l2, t).toFixed(1)}%)`;
}

function skyAt(phase) {
  const p = ((phase % 1) + 1) % 1;
  for (let i = 0; i < SKY_PHASES.length - 1; i++) {
    const a = SKY_PHASES[i];
    const b = SKY_PHASES[i + 1];
    if (p >= a.t && p <= b.t) {
      const local = (p - a.t) / (b.t - a.t);
      const top = lerpHsl(a.top, b.top, local);
      const mid = lerpHsl(a.mid, b.mid, local);
      const bot = lerpHsl(a.bot, b.bot, local);
      // Horizon glow = the bottom colour pushed lighter and slightly more
      // saturated, used for the atmospheric-perspective haze layer.
      const [h, s, l] = parseHsl(bot);
      const glowL = Math.min(90, l + 12);
      const glowS = Math.min(100, s + 10);
      const horizonGlow = `hsla(${h.toFixed(1)}, ${glowS.toFixed(1)}%, ${glowL.toFixed(1)}%, 0.7)`;
      return { top, mid, bot, horizonGlow };
    }
  }
  const last = SKY_PHASES[SKY_PHASES.length - 1];
  return { ...last, horizonGlow: `hsla(0, 0%, 0%, 0)` };
}

/** Fraction of the 24h day (0..1) for a given local Date. */
function fractionOfDay(date) {
  const secs = date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
  return secs / 86400;
}

/**
 * Compute the sky "phase" (0..1 index into SKY_PHASES) from wall-clock
 * minutes-since-sunrise / minutes-until-sunset. This gives correct
 * civil-twilight timing regardless of night length (which for Plett
 * ranges from ~10 hours in summer to ~14 hours in winter).
 *
 * Windows chosen to match observed dusk/dawn behaviour:
 *   * `-60 min → -30 min` before sunrise   → pre-dawn (0.22 → 0.27)
 *   * `-30 min → 0`       before sunrise   → sunrise glow (0.27 → 0.30)
 *   * `0     → +30 min`   after  sunrise   → golden hour (0.30 → 0.35)
 *   * mid-day interpolates 0.35 → 0.50 → 0.68 across the daylight span
 *   * `-30 min → 0`       before sunset    → late afternoon (0.68 → 0.75-ish)
 *   * `0     → +15 min`   after  sunset    → sunset glow fading (0.75)
 *   * `+15 → +60 min`     after  sunset    → nautical twilight (0.75 → 0.82)
 *   * `+60 → +90 min`     after  sunset    → deepening (0.82 → 0.95)
 *   * beyond ±90 min from either event      → deep night (1.00 / 0.00)
 */
function computePhase(now, sunriseISO, sunsetISO) {
  if (!sunriseISO || !sunsetISO) {
    return fractionOfDay(now); // fallback to clock-only
  }
  const t = now.getTime();
  const sr = new Date(sunriseISO).getTime();
  const ss = new Date(sunsetISO).getTime();
  const M = 60 * 1000; // one minute in ms

  // Before sunrise
  if (t < sr) {
    const minsBefore = (sr - t) / M;
    if (minsBefore > 90) return 0.00; // deep night
    if (minsBefore > 60) {
      // 90 → 60 min before sunrise: night → astronomical twilight
      return 0.00 + (1 - (minsBefore - 60) / 30) * 0.22;
    }
    if (minsBefore > 30) {
      // 60 → 30 min before sunrise: astronomical → civil dawn
      return 0.22 + (1 - (minsBefore - 30) / 30) * (0.27 - 0.22);
    }
    // 30 → 0 min before sunrise: sunrise glow builds
    return 0.27 + (1 - minsBefore / 30) * (0.30 - 0.27);
  }

  // After sunset
  if (t > ss) {
    const minsAfter = (t - ss) / M;
    if (minsAfter < 15) {
      // 0 → 15 min after sunset: still glowing
      return 0.75 - (minsAfter / 15) * 0.0; // hold at 0.75 briefly
    }
    if (minsAfter < 60) {
      // 15 → 60 min: nautical twilight
      return 0.75 + ((minsAfter - 15) / 45) * (0.82 - 0.75);
    }
    if (minsAfter < 90) {
      // 60 → 90 min: astronomical twilight
      return 0.82 + ((minsAfter - 60) / 30) * (0.95 - 0.82);
    }
    return 1.00; // deep night
  }

  // Daytime — split into morning ramp / plateau / afternoon ramp.
  const dayLen = ss - sr;
  const dayT = (t - sr) / dayLen; // 0..1 across daylight
  if (dayT < 0.10) {
    // First 10% of daylight: golden hour → morning
    return 0.30 + (dayT / 0.10) * (0.35 - 0.30);
  }
  if (dayT < 0.45) {
    // Morning → midday
    return 0.35 + ((dayT - 0.10) / 0.35) * (0.50 - 0.35);
  }
  if (dayT < 0.55) {
    // Around solar noon
    return 0.50;
  }
  if (dayT < 0.90) {
    // Midday → late afternoon
    return 0.50 + ((dayT - 0.55) / 0.35) * (0.68 - 0.50);
  }
  // Last 10%: late afternoon → sunset lead-in
  return 0.68 + ((dayT - 0.90) / 0.10) * (0.75 - 0.68);
}

/** Sun altitude 0..1 based on ISO sunrise/sunset. Returns null for night. */
function sunAltitude(now, sunriseISO, sunsetISO) {
  if (!sunriseISO || !sunsetISO) return null;
  const sr = new Date(sunriseISO).getTime();
  const ss = new Date(sunsetISO).getTime();
  const n = now.getTime();
  if (n < sr || n > ss) return null;
  const t = (n - sr) / (ss - sr); // 0 at sunrise, 1 at sunset
  // Parabolic arc so the sun peaks at noon.
  return 4 * t * (1 - t);
}

/** Moon altitude 0..1 during night (mirror of sun logic). */
function moonAltitude(now, sunsetISO, sunriseISO) {
  if (!sunsetISO || !sunriseISO) return null;
  const ss = new Date(sunsetISO).getTime();
  // The "next sunrise" is approximated by adding 24h to today's sunrise.
  const nextSr = new Date(sunriseISO).getTime() + 24 * 3600 * 1000;
  const n = now.getTime();
  if (n < ss || n > nextSr) return null;
  const t = (n - ss) / (nextSr - ss);
  return 4 * t * (1 - t);
}

// ── WMO code → scene flags ──────────────────────────────────────────────
// Reference: https://open-meteo.com/en/docs
function decodeCondition(code) {
  const c = Number(code);
  if (isNaN(c)) return { kind: "clear", intensity: 0 };
  if (c === 0) return { kind: "clear",     intensity: 0 };
  if (c <= 3)  return { kind: "cloudy",    intensity: (c) / 3 };            // 1..3
  if (c === 45 || c === 48) return { kind: "fog", intensity: c === 48 ? 1 : 0.7 };
  if (c >= 51 && c <= 57) return { kind: "rain", intensity: 0.35 };         // drizzle
  if (c >= 61 && c <= 67) return { kind: "rain", intensity: c >= 65 ? 1 : 0.7 }; // rain
  if (c >= 71 && c <= 77) return { kind: "snow", intensity: c >= 75 ? 1 : 0.6 };
  if (c >= 80 && c <= 82) return { kind: "rain", intensity: c === 82 ? 1 : 0.6 }; // showers
  if (c >= 85 && c <= 86) return { kind: "snow", intensity: 0.7 };
  if (c >= 95) return { kind: "thunder", intensity: 1 };
  return { kind: "clear", intensity: 0 };
}

// ── Precipitation canvas ────────────────────────────────────────────────
function PrecipCanvas({ kind, intensity, wind }) {
  const ref = useRef(null);
  const rafRef = useRef(0);
  const dropsRef = useRef([]);
  const sizeRef = useRef({ w: 0, h: 0 });
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (kind === "clear" || kind === "cloudy" || kind === "fog" || reducedMotion) return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sizeRef.current = { w, h };

      // Populate drops. Rain gets more + faster than snow.
      const target = kind === "snow"
        ? Math.floor(80 * intensity)
        : Math.floor(220 * intensity);
      dropsRef.current = new Array(target).fill(0).map(() => makeDrop(kind, w, h, wind));
    };

    const tick = () => {
      const { w, h } = sizeRef.current;
      ctx.clearRect(0, 0, w, h);
      const windX = (Number(wind) || 0) * 0.03; // px/frame horizontal drift
      for (const d of dropsRef.current) {
        if (kind === "snow") {
          d.y += d.speed;
          d.x += windX * 0.5 + Math.sin((d.y + d.phase) * 0.02) * 0.4;
          if (d.y > h + 4) { d.y = -4; d.x = Math.random() * w; }
          ctx.beginPath();
          ctx.fillStyle = `rgba(255,255,255,${d.a})`;
          ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
          ctx.fill();
        } else {
          d.y += d.speed;
          d.x += windX;
          if (d.y > h + 10) { d.y = -20; d.x = Math.random() * w; }
          ctx.beginPath();
          ctx.strokeStyle = `rgba(180,210,240,${d.a})`;
          ctx.lineWidth = d.thickness;
          ctx.moveTo(d.x, d.y);
          ctx.lineTo(d.x + windX * 6, d.y + d.len);
          ctx.stroke();
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener("resize", resize);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [kind, intensity, wind, reducedMotion]);

  if (kind === "clear" || kind === "cloudy" || kind === "fog" || reducedMotion) return null;
  return <canvas ref={ref} className="pointer-events-none fixed inset-0" style={{ zIndex: 1 }} data-testid="weather-precip" />;
}

function makeDrop(kind, w, h, wind) {
  if (kind === "snow") {
    return {
      x: Math.random() * w,
      y: Math.random() * h,
      r: 1 + Math.random() * 2.2,
      a: 0.5 + Math.random() * 0.4,
      speed: 0.4 + Math.random() * 1.2,
      phase: Math.random() * 100,
    };
  }
  return {
    x: Math.random() * w,
    y: Math.random() * h,
    len: 10 + Math.random() * 14,
    thickness: 0.8 + Math.random() * 0.8,
    a: 0.28 + Math.random() * 0.28,
    speed: 8 + Math.random() * 10,
  };
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(m.matches);
    const onChange = () => setReduced(m.matches);
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

// ── Cloud layer ─────────────────────────────────────────────────────────
// Individual soft "puff" clouds distributed across the sky. Each puff is
// a small container of overlapping radial gradients (feathered with a
// blur filter) — the same technique Apple Weather uses. Because each
// cloud is a *discrete shape* with real gaps between them, the sky
// reads correctly through the gaps instead of the whole viewport looking
// like a rectangular noise sheet.
//
// Cloud count, opacity, and vertical spread scale with cloud coverage.
// At night the tint is a subtle moon-lit blue-grey so clouds remain
// visible against the dark sky without looking like inky blotches.
function CloudLayer({ coverage, wind, isDay }) {
  const speed = 90 + (100 - Math.min(wind, 40)); // seconds per lap (base)
  // Cloud tint. Day → warm-white. Night → cool moon-lit steel blue but
  // *lighter* than the deep-night sky so puffs read as luminous, not as
  // dark holes.
  const tintRgb = isDay ? "255, 255, 255" : "170, 185, 210";
  // Layer opacity — bounded so heavy overcast never obscures the sky
  // entirely at night (looks wrong when there's no light source).
  const layerOpacity = isDay
    ? Math.min(0.9, 0.2 + (coverage / 100) * 0.7)
    : Math.min(0.55, 0.1 + (coverage / 100) * 0.4);

  // Number of clouds: 2 for wisps, up to 7 for heavy overcast.
  const cloudCount = Math.max(2, Math.round(2 + (coverage / 100) * 5));

  // Deterministic pseudo-random spread so clouds don't reshuffle every
  // render (would cause jitter on every state tick). Using `coverage` as
  // the seed keeps positions stable across time-of-day updates.
  const clouds = useMemo(() => {
    const rng = mulberry32(0xa17e11 ^ Math.round(coverage));
    const list = [];
    for (let i = 0; i < cloudCount; i++) {
      // Even x distribution with jitter so clouds don't clump.
      const xBase = (i + 0.5) / cloudCount * 100;
      const xJitter = (rng() - 0.5) * (60 / cloudCount);
      list.push({
        id: i,
        x: xBase + xJitter,           // % across viewport
        y: 8 + rng() * 42,             // % from top (upper 50%)
        scale: 0.75 + rng() * 1.15,   // size multiplier
        opacity: 0.55 + rng() * 0.4,
        duration: speed * (0.75 + rng() * 0.9),
        direction: rng() > 0.5 ? 1 : -1,
        delay: -rng() * 40,           // negative delay = animation is mid-cycle
      });
    }
    return list;
  }, [cloudCount, speed, coverage]);

  return (
    <div className="pointer-events-none absolute inset-0" style={{ opacity: layerOpacity }} data-testid="weather-clouds">
      <style>{`
        @keyframes cloudDriftR { from { transform: translate3d(-8vw, 0, 0); } to { transform: translate3d(8vw, 0, 0); } }
        @keyframes cloudDriftL { from { transform: translate3d(8vw, 0, 0); } to { transform: translate3d(-8vw, 0, 0); } }
        @media (prefers-reduced-motion: reduce) {
          .wx-cloud-puff { animation: none !important; }
        }
      `}</style>
      {clouds.map((c) => (
        <SoftCloud
          key={c.id}
          x={c.x}
          y={c.y}
          scale={c.scale}
          opacity={c.opacity}
          tintRgb={tintRgb}
          duration={c.duration}
          direction={c.direction}
          delay={c.delay}
        />
      ))}
    </div>
  );
}

/**
 * A single soft cloud puff. Rendered as a small container with 5-6
 * overlapping radial gradients that together form a natural,
 * multi-lobed cloud silhouette. The whole thing is blurred so the
 * gradient bands read as a single soft mass rather than distinct
 * ellipses.
 *
 * We deliberately AVOID `feTurbulence`/fractal-noise here. The previous
 * implementation used noise stretched across a full-width rect, which
 * produced blotchy dark bands. Radial gradients give a much cleaner
 * Apple-Weather look and are cheaper to render.
 */
function SoftCloud({ x, y, scale, opacity, tintRgb, duration, direction, delay }) {
  // A cloud is ~300×110px at scale 1 — roughly the aspect ratio of a
  // real cumulus formation. The outer div handles positioning and drift
  // animation; the inner div paints the puff itself.
  const width = 320 * scale;
  const height = 110 * scale;
  const anim = direction > 0 ? "cloudDriftR" : "cloudDriftL";
  return (
    <div
      className="wx-cloud-puff absolute"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${width}px`,
        height: `${height}px`,
        transform: "translate(-50%, -50%)",
        opacity,
        animation: `${anim} ${duration}s ease-in-out ${delay}s infinite alternate`,
        willChange: "transform",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(ellipse 38% 62% at 22% 62%, rgba(${tintRgb}, 0.85) 0%, rgba(${tintRgb}, 0) 68%),
            radial-gradient(ellipse 34% 78% at 42% 45%, rgba(${tintRgb}, 0.95) 0%, rgba(${tintRgb}, 0) 62%),
            radial-gradient(ellipse 30% 68% at 62% 40%, rgba(${tintRgb}, 0.9) 0%, rgba(${tintRgb}, 0) 65%),
            radial-gradient(ellipse 34% 58% at 78% 58%, rgba(${tintRgb}, 0.8) 0%, rgba(${tintRgb}, 0) 65%),
            radial-gradient(ellipse 26% 48% at 15% 74%, rgba(${tintRgb}, 0.65) 0%, rgba(${tintRgb}, 0) 68%),
            radial-gradient(ellipse 24% 46% at 88% 72%, rgba(${tintRgb}, 0.6) 0%, rgba(${tintRgb}, 0) 68%)
          `,
          filter: "blur(6px)",
        }}
      />
    </div>
  );
}

/** Tiny seeded PRNG so cloud positions stay stable between renders. */
function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Star field ──────────────────────────────────────────────────────────
function Stars({ opacity }) {
  const stars = useMemo(() => {
    return new Array(80).fill(0).map(() => ({
      x: Math.random() * 100,
      y: Math.random() * 60,   // upper 60% of screen only
      r: 0.4 + Math.random() * 1.1,
      a: 0.4 + Math.random() * 0.6,
      d: 2 + Math.random() * 4, // twinkle duration seconds
    }));
  }, []);
  if (opacity <= 0.02) return null;
  return (
    <svg className="pointer-events-none absolute inset-0" width="100%" height="100%" style={{ opacity }} data-testid="weather-stars" aria-hidden>
      <style>{`@keyframes twinkle { 0%,100%{opacity:0.35} 50%{opacity:1} }`}</style>
      {stars.map((s, i) => (
        // Deterministic composite key so React reconciler doesn't shuffle stars on re-render.
        <circle key={`star-${s.x}-${s.y}-${i}`} cx={`${s.x}%`} cy={`${s.y}%`} r={s.r} fill="white" opacity={s.a} style={{ animation: `twinkle ${s.d}s ease-in-out infinite` }} />
      ))}
    </svg>
  );
}

// ── Main component ─────────────────────────────────────────────────────
export function WeatherBackdrop() {
  const [wx, setWx] = useState(null);
  const [now, setNow] = useState(new Date());

  // Load weather. Same endpoint the WeatherChip uses — the axios cache
  // (via api.get) dedupes if both mount together. Refresh every 5 min.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get("/weather");
        if (!cancelled) setWx(res.data);
      } catch (_) { /* silent. backdrop just falls back to time-of-day only */ }
    };
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Tick every minute so the sky gradient drifts smoothly through the day.
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  const { skyStyle, sunPos, moonPos, starOpacity, sky } = useMemo(() => {
    // ── Compute the sky phase from Plett's real sunrise/sunset ──
    // computePhase() returns 0..1 with civil-twilight windows scaled by
    // wall-clock minutes, not fractional-night — so 90 min after sunset
    // is always properly dark regardless of season.
    const phase = wx?.sunrise && wx?.sunset
      ? computePhase(now, wx.sunrise, wx.sunset)
      : fractionOfDay(now);

    const sunAlt = wx ? sunAltitude(now, wx.sunrise, wx.sunset) : null;
    const moonAlt = wx ? moonAltitude(now, wx.sunset, wx.sunrise) : null;

    const sky = skyAt(phase);
    // Dampen saturation + brightness for heavy overcast so a rainy day
    // reads as grey-toned, not sunny-with-rain-on-top. We apply this
    // AFTER the phase interpolation so the time-of-day hue is preserved.
    const skyStyle = {
      background: `linear-gradient(180deg, ${sky.top} 0%, ${sky.mid} 55%, ${sky.bot} 100%)`,
    };

    // Sun / moon horizontal position — parametric across the arc.
    let sunPos = null;
    if (sunAlt !== null && wx?.sunrise && wx?.sunset) {
      const sr = new Date(wx.sunrise).getTime();
      const ss = new Date(wx.sunset).getTime();
      const t = (now.getTime() - sr) / (ss - sr); // 0..1 sunrise→sunset
      sunPos = { x: 10 + t * 80, y: 78 - sunAlt * 58 };
    }
    const moonPos = moonAlt !== null ? { x: 15 + moonAlt * 70, y: 70 - moonAlt * 45 } : null;

    // Stars fade in as we approach and pass sunset. Full opacity by
    // phase 0.90 (deep-twilight); zero during full daylight.
    let starOpacity = 0;
    if (phase < 0.22 || phase > 0.82) starOpacity = 1.0;
    else if (phase < 0.30) starOpacity = (0.30 - phase) / 0.08;
    else if (phase > 0.72) starOpacity = Math.min(1, (phase - 0.72) / 0.10);

    return { skyStyle, sunPos, moonPos, starOpacity, phase, sky };
  }, [wx, now]);

  const cond = decodeCondition(wx?.weather_code);
  const cloudCoverage = wx?.cloud_cover_pct ?? (cond.kind === "cloudy" ? 60 : 0);
  const isDay = sunPos !== null;

  // Toggle a body data attribute while mounted so the AppShell's opaque
  // `bg-background` becomes transparent — otherwise the fixed backdrop
  // would sit behind an opaque layer and never render. See
  // src/index.css for the matching rule.
  useEffect(() => {
    const prev = document.body.getAttribute("data-weather-backdrop");
    document.body.setAttribute("data-weather-backdrop", "on");
    return () => {
      if (prev == null) document.body.removeAttribute("data-weather-backdrop");
      else document.body.setAttribute("data-weather-backdrop", prev);
    };
  }, []);

  const backdrop = (
    <div
      className="pointer-events-none fixed inset-0 overflow-hidden"
      aria-hidden
      data-testid="weather-backdrop"
      data-condition={cond.kind}
      data-is-day={isDay ? "1" : "0"}
      style={{
        ...skyStyle,
        zIndex: 0,
        // Overcast / rainy / thunderous conditions dampen chroma and
        // brightness so the whole scene reads as grey-toned. Snow keeps
        // full brightness because a snowy scene should feel crisp.
        filter:
          cond.kind === "thunder" ? "saturate(0.55) brightness(0.6)"
          : cond.kind === "rain"   ? "saturate(0.7) brightness(0.75)"
          : cloudCoverage > 80     ? "saturate(0.82) brightness(0.88)"
          : "none",
        transition: "filter 1.2s ease",
      }}
    >
      {/* Atmospheric horizon haze. a soft warm/cool band along the bottom
          third that gives the sky depth (mimics how Earth's atmosphere
          scatters light at the horizon). Colour follows the sky's own
          bottom hue but pushed slightly brighter, then feathered up. */}
      <div
        className="absolute inset-x-0 bottom-0 h-[45%]"
        style={{
          background: `linear-gradient(to top, ${sky.horizonGlow} 0%, hsla(0, 0%, 100%, 0) 100%)`,
          mixBlendMode: "screen",
          opacity: 0.55,
        }}
        aria-hidden
      />
      {/* Sun. hidden during heavy overcast / precipitation because a
          crisp sun above a rain layer looks jarring. Still shown for
          clear + light-cloud conditions. */}
      {sunPos && cond.kind !== "rain" && cond.kind !== "thunder" && cond.kind !== "snow" && (
        <div
          className="absolute rounded-full"
          style={{
            left: `${sunPos.x}%`,
            top: `${sunPos.y}%`,
            transform: "translate(-50%, -50%)",
            width: "110px",
            height: "110px",
            // Dim the sun a little when we've got moderate cloud cover
            // so it reads as "hazy" rather than punching through overcast.
            opacity: cloudCoverage > 60 ? 0.55 : 1,
            background: "radial-gradient(circle at 50% 50%, hsla(50, 100%, 92%, 1) 0%, hsla(48, 100%, 78%, 0.95) 22%, hsla(38, 95%, 65%, 0.55) 48%, hsla(30, 90%, 60%, 0) 78%)",
            filter: "blur(0.5px)",
            // A subtle bloom for the "sun disc" feel Apple uses.
            boxShadow: "0 0 60px 20px hsla(50, 100%, 78%, 0.25)",
          }}
          data-testid="weather-sun"
        />
      )}
      {/* Moon */}
      {moonPos && (
        <div
          className="absolute w-16 h-16 rounded-full"
          style={{
            left: `${moonPos.x}%`,
            top: `${moonPos.y}%`,
            transform: "translate(-50%, -50%)",
            background: "radial-gradient(circle at 35% 40%, hsl(45, 20%, 92%) 0%, hsl(45, 15%, 82%) 55%, hsla(45, 15%, 82%, 0) 75%)",
            boxShadow: "0 0 24px 4px hsla(45, 20%, 92%, 0.35)",
          }}
          data-testid="weather-moon"
        />
      )}
      {/* Stars */}
      <Stars opacity={starOpacity} />
      {/* Clouds */}
      {(cond.kind === "cloudy" || cond.kind === "rain" || cond.kind === "thunder" || cond.kind === "snow" || cloudCoverage > 15) && (
        <CloudLayer coverage={Math.max(cloudCoverage, cond.kind === "cloudy" ? 40 : 0)} wind={wx?.wind_kmh ?? 5} isDay={isDay} />
      )}
      {/* Fog / mist */}
      {cond.kind === "fog" && (
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse at 50% 60%, hsla(220,10%,90%,0.75) 0%, hsla(220,10%,90%,0.35) 40%, hsla(220,10%,90%,0) 75%)",
          }}
          data-testid="weather-fog"
        />
      )}
      {/* Precipitation */}
      <PrecipCanvas kind={cond.kind} intensity={cond.intensity} wind={wx?.wind_kmh ?? 5} />
      {/* Subtle vignette so overlaid dashboard content stays readable */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0) 40%, rgba(0,0,0,0.18) 100%)",
        }}
      />
      {/* Overlay for readability on the dashboard cards themselves is
          handled by the caller — we don't tint uniformly here because
          that would flatten the sky's colour language. */}
    </div>
  );

  // Portal to document.body so the fixed backdrop lives outside the
  // AppShell's opaque stacking context and can actually be visible.
  if (typeof document === "undefined") return null;
  return createPortal(backdrop, document.body);
}
